/**
 * 교육실적 그리드 "강사" 셀의 팝업 에디터.
 *
 * 목록 API 는 강사 이름을 콤마로 이어붙인 표시용 문자열(mainTutorName/subTutorName)만 주고
 * tutorId 가 없어 편집을 시작할 수 없다. 팝업이 열리는 시점에 상세(getProgramDetail)를 한 번
 * 조회해 실제 강사 매칭 배열(tutors)을 받아온 뒤 그 위에서 편집한다.
 *
 * 검증 규칙은 상세 화면(TutorListForm)과 동일하게 맞춘다 — 주강사 1명 이상,
 * 주강사 강의시간 합계 = 교육시간(classHours).
 *
 * 그리드에 새로 추가한 행(아직 서버에 저장 안 된 임시 음수 id)은 조회할 상세가 없으므로
 * 빈 목록에서 시작한다. 등록(POST) 자체는 강사 매칭 없이도 가능하다(programEditSpec.validateProgramRow
 * 가 tutors 를 제외).
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Button, Select, InputNumber, Spin } from 'antd'

import { getProgramDetail } from 'api/program.api'
import { getTutorRegionList } from 'api/tutor.api'
import { GrayTrashIcon24 } from 'assets/icons'

const TUTOR_TYPE_OPTIONS = [
    { value: '주강사', label: '주강사' },
    { value: '보조강사', label: '보조강사' },
]

const withRowId = t => ({ ...t, id: t.id || uuidv4() })

const getMainTutorTotalHours = list =>
    list
        .filter(t => t.tutorType === '주강사' && !Number.isNaN(parseFloat(t.classHours)))
        .reduce((acc, cur) => acc + parseFloat(cur.classHours || 0), 0)

/** 확인 시점 검증. '' 통과 / 문자열 = 에러메시지. programEditSpec 의 PROGRAM_FIELD_SPEC.tutors 와 규칙을 맞춘다. */
export const validateTutorsList = (list, classHours) => {
    if (!Array.isArray(list) || list.length === 0) return '주강사를 1명 이상 등록하세요.'
    if (list.some(t => !t.tutorId)) return '강사를 선택하지 않은 행이 있습니다.'
    const mainCount = list.filter(t => t.tutorType === '주강사').length
    if (mainCount === 0) return '주강사를 1명 이상 등록하세요.'
    const total = getMainTutorTotalHours(list)
    if (Number(total) !== Number(classHours)) {
        return `주강사 강의시간 합계(${total})가 교육시간(${classHours ?? 0})과 일치해야 합니다.`
    }
    return ''
}

const TutorMatchCellEditor = forwardRef((params, ref) => {
    const { data } = params
    const registrationYear = data?.registrationYear
    const classHours = data?.classHours

    /*
     * antd Select 의 드롭다운은 기본적으로 document.body 에 포털로 붙는다.
     * ag-grid 팝업 에디터는 "에디터 DOM 바깥 클릭"을 감지하면 즉시 편집을 종료하는데,
     * body 에 붙은 드롭다운 옵션은 에디터 DOM의 자손이 아니라서 그 클릭이 바깥 클릭으로
     * 잡혀 옵션을 고르자마자 팝업 전체가 닫혀버린다.
     * getPopupContainer 로 드롭다운을 이 컴포넌트 루트 안에 붙여서 "에디터 내부 클릭"으로 만든다.
     */
    const containerRef = useRef(null)
    const getPopupContainer = () => containerRef.current

    const [loading, setLoading] = useState(true)
    const [tutorList, setTutorList] = useState([])
    const [optionTutor, setOptionTutor] = useState([])
    const [error, setError] = useState('')

    // 이미 이번 세션에서 한 번 편집해 배열이 셀 값으로 남아 있으면 재조회하지 않는다.
    // 그리드에서 새로 추가한 행(임시 음수 id, 아직 서버에 없음)은 상세 조회 대상이 없으므로
    // 빈 목록에서 시작한다 — 등록 시 함께 저장된다(등록 자체는 강사 없이도 가능, programEditSpec 참고).
    useEffect(() => {
        let ignore = false
        const isUnsavedRow = !data?.programId || Number(data.programId) < 0
        const load = async () => {
            setLoading(true)
            try {
                if (Array.isArray(params.value)) {
                    if (!ignore) setTutorList(params.value.map(withRowId))
                } else if (isUnsavedRow) {
                    if (!ignore) setTutorList([])
                } else {
                    const res = await getProgramDetail(data.programId)
                    const tutors = res?.data?.data?.tutors || []
                    if (!ignore) setTutorList(tutors.map(withRowId))
                }
            } catch (e) {
                if (!ignore) setError('강사 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => {
            ignore = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let ignore = false
        const fetchOptions = async () => {
            try {
                const response = await getTutorRegionList(registrationYear)
                if (response.status === 200 && !ignore) {
                    const list = response.data.data || []
                    setOptionTutor(
                        list.map(t => ({
                            value: t.tutorId,
                            label: t.name,
                            tutorId: t.tutorId,
                            tutorName: t.name,
                        })),
                    )
                }
            } catch (e) {
                // 강사 검색 옵션 조회 실패는 편집 자체를 막지 않는다.
            }
        }
        fetchOptions()
        return () => {
            ignore = true
        }
    }, [registrationYear])

    const updateRow = (id, field, value) => {
        setTutorList(list => list.map(t => (t.id === id ? { ...t, [field]: value } : t)))
    }

    const updateTutorSelection = (id, tutorId, option) => {
        setTutorList(list =>
            list.map(t => (t.id === id ? { ...t, tutorId, tutorName: option?.tutorName ?? '' } : t)),
        )
    }

    const addRow = () => {
        setTutorList(list => [...list, { id: uuidv4(), tutorId: '', tutorName: '', tutorType: '주강사', classHours: null }])
    }

    const removeRow = id => {
        setTutorList(list => list.filter(t => t.id !== id))
    }

    const mainTotal = useMemo(() => getMainTutorTotalHours(tutorList), [tutorList])
    const isMismatch = Number(mainTotal) !== Number(classHours ?? 0)

    /*
     * ag-grid 의 표준 "에디터에서 값 회수"(stopEditing → cellComp.getCellEditor().getValue()) 경로가
     * 이 팝업 에디터에서는 값을 못 찾고 조용히 버린다(콘솔 디버깅으로 확인 — isCancelAfterEnd/getValue
     * 가 아예 호출되지 않음, 원인 불명). 그 경로에 기대지 않고 행 API로 값을 직접 커밋한다.
     * → stopEditing() 은 여전히 그 회수를 시도하므로, 이미 커밋했다는 뜻으로 취소 플래그를 세워
     *   두 번째(빈) 시도가 방금 넣은 값을 다시 검증해 헛된 에러 토스트를 띄우지 않게 막는다.
     */
    const cancelledRef = useRef(false)

    const handleConfirm = () => {
        const msg = validateTutorsList(tutorList, classHours)
        if (msg) {
            setError(msg)
            return
        }
        setError('')
        params.node.setDataValue(params.column, tutorList)
        cancelledRef.current = true
        params.stopEditing()
    }

    const handleCancel = () => {
        cancelledRef.current = true
        params.stopEditing()
    }

    useImperativeHandle(
        ref,
        () => ({
            getValue: () => tutorList,
            isPopup: () => true,
            isCancelBeforeStart: () => false,
            isCancelAfterEnd: () => cancelledRef.current,
        }),
        [tutorList],
    )

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: 540,
                maxHeight: 480,
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                // 드롭다운을 이 안에 붙이므로(getPopupContainer) 잘리지 않게 overflow 를 hidden 으로 두지 않는다.
            }}
        >
            {/* 헤더 — 스크롤과 무관하게 항상 보임 */}
            <div style={{ padding: '14px 16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>강사 관리</span>
                    <Button
                        type="text"
                        size="small"
                        onClick={handleCancel}
                        style={{ padding: 0, width: 24, height: 24, color: '#999', fontSize: 14 }}
                    >
                        ✕
                    </Button>
                </div>
                <div style={{ fontSize: 12, color: '#888', margin: '4px 0 12px', lineHeight: 1.5 }}>
                    주강사는 1명 이상 등록해야 하며, 주강사 강의시간 합계는 교육시간과 같아야 합니다.
                </div>
                {!loading && tutorList.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#aaa', paddingBottom: 6 }}>
                        <div style={{ width: 100 }}>강사유형</div>
                        <div style={{ width: 176 }}>강사</div>
                        <div style={{ width: 100 }}>강의시간</div>
                    </div>
                )}
            </div>

            {/* 목록 — 이 영역만 스크롤 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        {tutorList.map(tutor => (
                            <div
                                key={tutor.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    paddingBottom: 10,
                                    marginBottom: 10,
                                    borderBottom: '1px solid #f5f5f5',
                                }}
                            >
                                <Select
                                    value={tutor.tutorType}
                                    options={TUTOR_TYPE_OPTIONS}
                                    onChange={value => updateRow(tutor.id, 'tutorType', value)}
                                    getPopupContainer={getPopupContainer}
                                    style={{ width: 100 }}
                                />
                                <Select
                                    showSearch
                                    value={tutor.tutorId || undefined}
                                    placeholder="강사 선택"
                                    options={optionTutor}
                                    onChange={(value, option) => updateTutorSelection(tutor.id, value, option)}
                                    getPopupContainer={getPopupContainer}
                                    style={{ width: 176 }}
                                    filterOption={(input, option) => (option?.label ?? '').includes(input)}
                                />
                                <InputNumber
                                    min={0}
                                    value={tutor.classHours}
                                    onChange={value => updateRow(tutor.id, 'classHours', value)}
                                    placeholder="강의시간"
                                    formatter={v => (v || v === 0 ? `${v}시간` : '')}
                                    parser={v => String(v ?? '').replace('시간', '')}
                                    style={{ width: 100 }}
                                />
                                <Button
                                    type="text"
                                    onClick={() => removeRow(tutor.id)}
                                    icon={<GrayTrashIcon24 />}
                                    style={{
                                        padding: 0,
                                        width: 32,
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                />
                            </div>
                        ))}

                        <Button type="dashed" size="small" onClick={addRow} style={{ marginBottom: 12 }}>
                            + 강사 추가
                        </Button>
                    </>
                )}
            </div>

            {/* 푸터 — 확인/취소는 스크롤 없이 항상 보이게 고정 */}
            {!loading && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 16px' }}>
                    {error && <div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{error}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 12, color: isMismatch ? '#ff4d4f' : '#555' }}>
                            주강사 강의시간 합계: <b>{mainTotal}</b> / 교육시간: <b>{classHours ?? 0}</b>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button onClick={handleCancel}>취소</Button>
                            <Button type="primary" onClick={handleConfirm}>
                                확인
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
})

TutorMatchCellEditor.displayName = 'TutorMatchCellEditor'

export default TutorMatchCellEditor
